import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { FadeIn, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import { IMAGES } from '../lib/images';
import {
  ArrowRight, Shield, Users, BarChart3, Bell, Target, TrendingUp,
  Eye, RefreshCw, Heart, DollarSign, Zap, CheckCircle2, XCircle,
  CreditCard, Building2, Lock, Wifi, MonitorSmartphone, Quote, Play,
  Clock, Award, UserCheck
} from 'lucide-react';

/* ─── HERO ─── */
const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-14 sm:pb-16">
    <div className="absolute inset-0 -z-10" style={{
      background: 'radial-gradient(1200px circle at 20% 10%, rgba(42,140,134,0.14), transparent 55%), radial-gradient(900px circle at 80% 0%, rgba(242,107,91,0.10), transparent 50%)'
    }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 items-center">
      <FadeInLeft className="lg:col-span-6">
        {/* Power positioning badge */}
        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--sss-navy))] text-white px-4 py-1.5 mb-5">
          <Award className="h-3.5 w-3.5 text-[hsl(var(--sss-coral))]" />
          <span className="text-xs font-medium">The Patient Recovery Engine for Dental Practices</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[hsl(var(--sss-navy))] leading-[1.1]">
          Recycle Dead Leads Into Life-Changing Cases
        </h1>
        <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70 max-w-prose leading-relaxed">
          Every patient declined for financing is revenue your practice already paid to acquire — then lost. Smile Score Solutions takes those "impossible" leads and gives them the tools, guidance, and monitoring to become qualified patients who come back ready for treatment.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/contact">
            <Button data-testid="hero-book-demo-button" className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-8 h-12 text-base font-medium shadow-md hover:shadow-lg transition-shadow">
              Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Button data-testid="hero-get-started-button" variant="outline" className="border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl px-8 h-12 text-base font-medium"
            onClick={() => window.open('https://approvemysmile.com/register/personal', '_blank')}>
            Get Started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        {/* Trust bar */}
        <div className="mt-10 rounded-2xl border border-[hsl(var(--border))] bg-white/70 backdrop-blur px-4 py-3 shadow-[var(--shadow-sm)]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: <CreditCard className="h-4 w-4" />, label: 'Credit Monitoring From Top Bureaus' },
              { icon: <MonitorSmartphone className="h-4 w-4" />, label: 'Practice-Branded Portal' },
              { icon: <Zap className="h-4 w-4" />, label: 'AI-Powered Guidance' },
              { icon: <DollarSign className="h-4 w-4" />, label: '$249/mo Unlimited Patients' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs sm:text-sm text-[hsl(var(--sss-navy))]/80">
                <span className="text-[hsl(var(--sss-teal))]">{item.icon}</span>{item.label}
              </div>
            ))}
          </div>
        </div>
      </FadeInLeft>
      {/* Hero visual */}
      <FadeInRight className="lg:col-span-6">
        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-[320px] sm:h-[420px]">
            <img src={IMAGES.dentistExplainingXray} alt="Dentist consulting with patient" className="w-full h-full object-cover" loading="eager" />
          </div>
          {/* Floating notification */}
          <div className="absolute -bottom-4 -left-4 sm:-left-8 bg-white rounded-xl shadow-[var(--shadow-md)] border border-[hsl(var(--border))] p-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
              <Bell className="h-4 w-4 text-[hsl(var(--sss-teal))]" />
            </div>
            <div>
              <p className="text-xs font-medium text-[hsl(var(--sss-navy))]">Patient Ready!</p>
              <p className="text-[10px] text-muted-foreground">Score crossed threshold</p>
            </div>
          </div>
          {/* Floating stat */}
          <div className="absolute -top-3 -right-3 sm:-right-6 bg-white rounded-xl shadow-[var(--shadow-md)] border border-[hsl(var(--border))] p-3 text-center">
            <p className="text-2xl font-bold text-[hsl(var(--sss-coral))]">+42</p>
            <p className="text-[10px] text-muted-foreground">pts avg gain</p>
          </div>
        </div>
      </FadeInRight>
    </div>
  </section>
);

/* ─── WAITLIST BANNER (ORCAA First Look) ─── */
const WaitlistBanner = () => (
  <section className="py-8 sm:py-10">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <div
          data-testid="home-waitlist-banner"
          className="relative overflow-hidden rounded-2xl bg-[hsl(var(--sss-navy))] noise-overlay px-6 sm:px-10 py-8 sm:py-10 shadow-[var(--shadow-lg)]"
        >
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(700px circle at 90% 20%, rgba(42,140,134,0.3), transparent 55%)'
          }} />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--sss-coral))]/15 border border-[hsl(var(--sss-coral))]/30 px-3.5 py-1 mb-4">
                <Zap className="h-3 w-3 text-[hsl(var(--sss-coral))]" />
                <span className="text-[11px] font-semibold tracking-wide text-white uppercase">New · ORCAA First Look</span>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-white leading-tight">
                Full-Arch Case Recovery Is Coming.
              </h2>
              <p className="mt-2 text-sm sm:text-base text-white/70 max-w-2xl">
                They said yes to treatment — their credit said no. A limited group of full-arch practices
                is being invited onto the Early Access Waiting List. Performance-based: you only pay when you get paid.
              </p>
            </div>
            <div className="shrink-0">
              <Link to="/waitlist">
                <Button
                  data-testid="home-waitlist-banner-button"
                  className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-8 h-12 text-base font-medium shadow-md hover:shadow-lg transition-shadow w-full lg:w-auto"
                >
                  Join the Waiting List <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <p className="mt-2 text-center text-[11px] text-white/50">Free to join · Priority onboarding</p>
            </div>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── SOCIAL PROOF NUMBERS ─── */
const SocialProofBar = () => (
  <section className="py-8 sm:py-10 border-y border-[hsl(var(--border))] bg-white">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { num: '50%+', label: 'of financing applications get declined', icon: <Users className="h-5 w-5" /> },
            { num: '$5K–$30K', label: 'value of one recovered implant case', icon: <DollarSign className="h-5 w-5" /> },
            { num: '10x', label: 'potential ROI from a single recovered case', icon: <TrendingUp className="h-5 w-5" /> },
            { num: '30 sec', label: 'to enroll a patient from your chair', icon: <Clock className="h-5 w-5" /> },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <span className="text-[hsl(var(--sss-teal))]">{item.icon}</span>
              <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--sss-navy))]">{item.num}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{item.label}</p>
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── VIDEO SECTION ─── */
const VideoSection = () => (
  <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-10">
        <SectionLabel>See It In Action</SectionLabel>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          Watch How Smile Score Solutions Works
        </h2>
        <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70">
          See how dental practices are recovering lost revenue in under 2 minutes.
        </p>
      </FadeIn>
      <FadeIn className="max-w-4xl mx-auto">
        <div className="relative rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] border border-[hsl(var(--border))] bg-black aspect-video">
          <iframe
            src="https://player.vimeo.com/video/929966793?h=0ad0381901"
            title="Smile Score Solutions Demo"
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── THE REVENUE YOU'RE THROWING AWAY ─── */
const WhyNowSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--sss-coral))]/10 text-[hsl(var(--sss-coral))] px-4 py-1.5 mb-4">
            <Award className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold uppercase tracking-wide">The Untapped Opportunity</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            You're Sitting on a Goldmine of "Dead" Leads
          </h2>
          <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
            Right now, your practice is spending thousands on marketing to get patients through the door. They show up. They want treatment. Then they're declined for financing — and they vanish. You write them off as losses.
          </p>
          <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
            <span className="font-semibold text-[hsl(var(--sss-navy))]">What if you could recycle every single one of those leads?</span> Not with a phone call or a generic follow-up — but with a system that actually helps them become qualified, monitors their progress, and brings them back to your chair when they're ready.
          </p>
          <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
            That's not a dream. That's what Smile Score Solutions does — and nothing else in the market comes close.
          </p>
        </FadeInLeft>
        <FadeInRight>
          <div className="space-y-4">
            {[
              { stat: '36%', label: 'of U.S. adults have subprime credit scores (below 670)', source: 'Experian' },
              { stat: '50%+', label: 'of patients applying for dental financing are declined', source: 'Industry Data' },
              { stat: '175M', label: 'Americans currently unable to qualify for healthcare financing', source: 'Federal Reserve' },
              { stat: '~$500', label: 'average cost to acquire a single dental patient through marketing', source: 'Dental Marketing Benchmarks' },
            ].map((item, i) => (
              <Card key={i} className="rounded-xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="text-3xl font-bold text-[hsl(var(--sss-coral))] shrink-0 w-20 text-right">{item.stat}</div>
                  <div className="border-l border-[hsl(var(--border))] pl-4">
                    <p className="text-sm text-[hsl(var(--sss-ink))]/80">{item.label}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{item.source}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

/* ─── PROBLEM ─── */
const ProblemSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>The Challenge</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            These Patients Want Treatment. They Just Can't Get Approved — Yet.
          </h2>
          <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
            They showed up. They sat in your chair. They said yes to the treatment plan. Then the financing came back declined — and they walked out with no path forward.
          </p>
          <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
            Without a system to nurture these patients, they disappear. They delay treatment, go to a competitor, or give up entirely. Your marketing dollars are wasted.
          </p>
          <p className="mt-3 text-base font-semibold text-[hsl(var(--sss-navy))]">
            These aren't bad leads. They're untapped opportunities waiting for the right system.
          </p>
        </FadeInLeft>
        <FadeInRight>
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-48 sm:h-64">
              <img src={IMAGES.dentistConsulting} alt="Dental consultation" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { stat: '65%', desc: 'leads below financing thresholds' },
                { stat: '50%', desc: 'consults declined for financing' },
                { stat: '$0', desc: 'recovered without follow-up' },
              ].map((item, i) => (
                <Card key={i} className="rounded-xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] text-center p-3">
                  <CardContent className="p-0">
                    <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--sss-coral))]">{item.stat}</p>
                    <p className="mt-1 text-[10px] sm:text-xs text-muted-foreground leading-tight">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

/* ─── SOLUTION ─── */
const SolutionSection = () => {
  const features = [
    { icon: <MonitorSmartphone className="h-5 w-5" />, title: 'Practice-Branded Patient Portal', desc: 'Your own branded portal with financial wellness tools, educational resources, and a step-by-step action plan. All under your practice brand.' },
    { icon: <Bell className="h-5 w-5" />, title: 'Credit Monitoring & Practice Alerts', desc: "Ongoing credit monitoring powered by top credit bureaus. Get alerts when a patient's score crosses a financing threshold." },
    { icon: <Target className="h-5 w-5" />, title: 'Intelligent Guided Improvement', desc: 'Personalized recommendations, specific action steps, realistic timelines, and projected score impact. Adapted to each unique situation.' },
    { icon: <RefreshCw className="h-5 w-5" />, title: 'Automated Patient Nurture', desc: "Periodic communications that keep patients engaged. When they complete milestones, your office is notified automatically." },
  ];
  return (
    <section className="relative py-16 sm:py-20 bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center max-w-3xl mx-auto mb-12">
          <SectionLabel>The Solution</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            Recycle Every Declined Patient Into a Future Case
          </h2>
          <p className="mt-4 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">
            Instead of writing off declined patients, Smile Score Solutions gives them a guided path to financial readiness. They stay in your ecosystem until they qualify for the treatment they already want.
          </p>
        </FadeIn>
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          <FadeInLeft className="hidden lg:block lg:col-span-4">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-64">
                <img src={IMAGES.happySmileCloseup} alt="Happy patient smile" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-sm)] h-48">
                <img src={IMAGES.modernClinic} alt="Modern dental clinic" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </FadeInLeft>
          <StaggerContainer className="lg:col-span-8 grid gap-4 sm:gap-6 sm:grid-cols-2">
            {features.map((f, i) => (
              <StaggerItem key={i}>
                <Card data-testid="feature-card" className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                  <CardContent className="p-6">
                    <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal))] flex items-center justify-center mb-4">{f.icon}</div>
                    <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-2">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
};

/* ─── WITH VS WITHOUT COMPARISON ─── */
const ComparisonSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>The Difference</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          Same Patient. Two Very Different Outcomes.
        </h2>
      </FadeIn>
      <FadeIn>
        <div className="grid gap-6 sm:grid-cols-2 max-w-4xl mx-auto">
          {/* Without */}
          <Card className="rounded-2xl border-2 border-red-200 bg-red-50/30 shadow-[var(--shadow-sm)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <XCircle className="h-5 w-5 text-red-400" />
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))]">Without Smile Score Solutions</h3>
              </div>
              <div className="space-y-3">
                {[
                  'Patient is declined for financing',
                  'Walks out with no follow-up system',
                  'Finds a competitor or delays indefinitely',
                  'Your marketing spend is wasted',
                  'Revenue lost permanently',
                  'No visibility into their status',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/70">
                    <XCircle className="h-4 w-4 text-red-300 shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {/* With */}
          <Card className="rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-[hsl(var(--accent))]/30 shadow-[var(--shadow-md)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))]">With Smile Score Solutions</h3>
              </div>
              <div className="space-y-3">
                {[
                  'Patient is enrolled in your branded portal',
                  'Guided financial wellness journey begins',
                  'Stays connected to YOUR practice',
                  'Your marketing investment is protected',
                  'Revenue recovered when they qualify',
                  'Real-time alerts when they\'re ready',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── PATIENT PORTAL FEATURES ─── */
const PatientPortalSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>Inside the Patient Portal</SectionLabel>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          Powerful Tools Your Patients Actually Use
        </h2>
        <p className="mt-4 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">
          Every enrolled patient gets access to a practice-branded portal with real credit monitoring and intelligent guidance. No generic advice, no fluff.
        </p>
      </FadeIn>

      <FadeIn>
        <div className="grid gap-6 sm:grid-cols-2 max-w-5xl mx-auto">
          {/* Track tier */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1">
                <SectionLabel className="mb-0">Credit Monitoring</SectionLabel>
              </div>
              <h3 className="text-xl font-bold text-[hsl(var(--sss-navy))] mb-4">Track</h3>
              <Separator className="mb-5" />
              <div className="space-y-3 mb-6">
                {[
                  'Weekly Equifax Credit Report',
                  'Daily Credit Monitoring',
                  'Score Tracking & Trends',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-[hsl(var(--secondary))] p-4">
                <p className="text-xs text-[hsl(var(--sss-ink))]/60 italic leading-relaxed">
                  60% of people who actively monitor their credit score take positive financial actions vs. those who don't track it at all.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Grow tier */}
          <Card className="rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-white shadow-[var(--shadow-md)] relative">
            <div className="absolute -top-3 right-6">
              <Badge className="bg-[hsl(var(--sss-teal))] text-white text-xs px-3 py-1">Most Popular</Badge>
            </div>
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1">
                <SectionLabel className="mb-0">Credit Monitoring + AI</SectionLabel>
              </div>
              <h3 className="text-xl font-bold text-[hsl(var(--sss-navy))] mb-4">Grow</h3>
              <Separator className="mb-5" />
              <p className="text-xs text-muted-foreground mb-3 italic">Everything in Track, plus:</p>
              <div className="space-y-3 mb-6">
                {[
                  'AI Credit Agent personalized to their credit profile',
                  'Step-by-step guidance on how to manage their credit health',
                  'Actionable insights updated as their credit changes',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                    <Zap className="h-4 w-4 text-[hsl(var(--sss-coral))] shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-[hsl(var(--accent))]/50 p-4">
                <p className="text-xs text-[hsl(var(--sss-ink))]/60 italic leading-relaxed">
                  Patients with a personalized action plan are better equipped to understand and manage their credit profile than those monitoring alone.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </FadeIn>

      <FadeIn className="mt-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--accent))]/40 border border-[hsl(var(--sss-teal))]/20 px-5 py-3">
          <Award className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
          <p className="text-sm text-[hsl(var(--sss-navy))]">
            <span className="font-semibold">Premium Plan:</span> Give your hottest leads full <span className="font-semibold">Grow</span> access — AI-powered credit coaching included for your most qualified patients.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── HOW IT WORKS (Brief) ─── */
const HowItWorksBrief = () => {
  const steps = [
    { num: '1', title: 'Identify & Enroll', desc: 'Send declined patients a link to your branded portal. Takes 30 seconds via text, email, or in-office.' },
    { num: '2', title: 'Guided Journey Begins', desc: 'Credit monitoring, personalized improvement steps, educational content, and progress tracking. All practice-branded.' },
    { num: '3', title: 'Patient Returns Ready', desc: "Your practice gets alerts when scores improve. Reach out, patient returns approved. Revenue recovered." },
  ];
  return (
    <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center max-w-3xl mx-auto mb-12">
          <SectionLabel>How It Works</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
            Three Steps. Zero Complexity.
          </h2>
        </FadeIn>
        <StaggerContainer className="grid gap-6 sm:grid-cols-3">
          {steps.map((step, i) => (
            <StaggerItem key={i}>
              <Card data-testid="how-it-works-step" className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-full bg-[hsl(var(--sss-navy))] text-white flex items-center justify-center text-lg font-bold mb-4">{step.num}</div>
                  <h3 className="text-lg font-semibold text-[hsl(var(--sss-navy))] mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
        <FadeIn className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/how-it-works">
            <Button variant="outline" className="border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl px-6 h-11">
              See the Full Walkthrough <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-6 h-11">Book a Demo</Button>
          </Link>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── OUTCOMES ─── */
const OutcomesSection = () => {
  const outcomes = [
    { icon: <DollarSign className="h-5 w-5" />, title: 'Recover Lost Revenue', desc: 'Turn patients you already paid to acquire into accepted cases.' },
    { icon: <Users className="h-5 w-5" />, title: 'Keep Patients Connected', desc: 'Your branded portal keeps patients loyal — not searching competitors.' },
    { icon: <Eye className="h-5 w-5" />, title: 'Full Visibility', desc: 'Know exactly where every declined patient stands financially.' },
    { icon: <Bell className="h-5 w-5" />, title: 'Auto Reactivation', desc: 'Alerts when patients hit financing thresholds — reach out at the perfect moment.' },
    { icon: <TrendingUp className="h-5 w-5" />, title: 'Higher Case Acceptance', desc: 'Patients return with better scores and deeper loyalty to your practice.' },
    { icon: <Heart className="h-5 w-5" />, title: 'Change Lives Beyond Dental', desc: "Better credit doesn't just qualify patients for treatment — it opens doors to cars, housing, jobs, and real financial freedom. Your practice becomes the catalyst." },
  ];
  return (
    <section className="relative py-16 sm:py-20">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 items-center">
          <FadeInLeft className="lg:col-span-5">
            <SectionLabel>Results</SectionLabel>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Revenue for Your Practice. Life-Changing Impact for Patients.</h2>
            <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70 leading-relaxed">
              When patients work on their financial wellness for dental treatment, the benefits don't stop there. Better credit health means access to cars, housing, employment, and financial freedom. Your practice becomes the starting point for real change.
            </p>
            <div className="mt-6 rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-48 sm:h-64">
              <img src={IMAGES.happyManSmiling} alt="Happy patient after treatment" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </FadeInLeft>
          <StaggerContainer className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
            {outcomes.map((o, i) => (
              <StaggerItem key={i}>
                <Card data-testid="outcome-card" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                  <CardContent className="p-0">
                    <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal))] flex items-center justify-center mb-3">{o.icon}</div>
                    <h3 className="text-sm font-semibold text-[hsl(var(--sss-navy))] mb-1.5">{o.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{o.desc}</p>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
};

/* ─── WORKS WITH FINANCING ─── */
const FinancingPartnersSection = () => (
  <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 text-center">
      <FadeIn>
        <p className="text-sm font-medium text-muted-foreground mb-4">Works with all major dental financing providers</p>
        <div className="flex flex-wrap gap-4 sm:gap-8 justify-center items-center">
          {['CareCredit', 'Cherry', 'Proceed Finance', 'LendingClub', 'Sunbit', 'GreenSky', 'Alphaeon'].map((partner, i) => (
            <span key={i} className="text-base sm:text-lg font-semibold text-[hsl(var(--sss-navy))]/30">{partner}</span>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── WHO IT'S FOR ─── */
const AudienceSection = () => (
  <section className="relative py-16 sm:py-20 overflow-hidden">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 text-center">
      <FadeIn>
        <SectionLabel>Who It's For</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Built for Practices That Take Growth Seriously</h2>
        <p className="mt-4 text-base md:text-lg text-[hsl(var(--sss-ink))]/70 max-w-2xl mx-auto">
          Designed for any dental practice using third-party patient financing that wants to stop losing cases to declines.
        </p>
        <div className="flex flex-wrap gap-2 justify-center mt-8">
          {['General Dentists', 'Implant Centers', 'Oral Surgeons', 'Cosmetic Practices', 'DSOs', 'Multi-Location Groups', 'Treatment Coordinators', 'Office Managers'].map((a, i) => (
            <Badge key={i} data-testid="audience-badge" variant="outline" className="rounded-full border border-[hsl(var(--border))] bg-white text-[hsl(var(--sss-navy))] px-4 py-2 text-sm font-medium shadow-[var(--shadow-sm)]">{a}</Badge>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── TESTIMONIALS ─── */
const TestimonialsSection = () => {
  const testimonials = [
    { quote: "I am very pleased with the service since we started with Smile Score Solutions. We finally have a way to help patients get the care they need when they are declined for financing. After a few months we started seeing patients return with improved scores ready to start treatment. It's been worth every penny.", name: 'Dr. Sarah J.', title: 'Dentist', initials: 'SJ', color: 'bg-[hsl(var(--sss-teal))]' },
    { quote: "So many of our patients were sold on the treatment we were offering, but many could not get approved for financing. We simply send them the link and tell them to come back once their credit score improves. It's been amazing.", name: 'Dr. Elena M.', title: 'Dentist', initials: 'EM', color: 'bg-[hsl(var(--sss-coral))]' },
    { quote: "We started seeing patients return within a few months and get approved for financing. The best part is the process is completely automated.", name: 'Dr. Michael B.', title: 'Dentist', initials: 'MB', color: 'bg-[hsl(var(--sss-navy))]' },
    { quote: "I have already had patients come in after improving their credit. They've been able to get financed and schedule treatment. This is such a great resource.", name: 'Dr. Sarah P.', title: 'Dentist', initials: 'SP', color: 'bg-[hsl(var(--sss-teal-2))]' },
  ];
  return (
    <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center max-w-3xl mx-auto mb-12">
          <SectionLabel>What Doctors Are Saying</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Trusted by Dental Professionals</h2>
        </FadeIn>
        <StaggerContainer className="grid gap-4 sm:grid-cols-2">
          {testimonials.map((t, i) => (
            <StaggerItem key={i}>
              <Card data-testid="testimonial-card" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-6 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
                <CardContent className="p-0">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`h-10 w-10 rounded-full ${t.color} text-white flex items-center justify-center text-sm font-bold`}>{t.initials}</div>
                    <div>
                      <p className="text-sm font-semibold text-[hsl(var(--sss-navy))]">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.title}</p>
                    </div>
                    <Quote className="h-6 w-6 text-[hsl(var(--sss-coral))]/20 ml-auto" />
                  </div>
                  <p className="text-sm text-[hsl(var(--sss-ink))]/80 leading-relaxed italic">"{t.quote}"</p>
                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

/* ─── PRICING ─── */
const PricingSnapshot = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>Pricing</SectionLabel>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">One Plan. Unlimited Patients. No Surprises.</h2>
          <div className="mt-6 flex items-end gap-2">
            <span className="text-5xl font-bold text-[hsl(var(--sss-navy))]" data-testid="pricing-amount">$249</span>
            <span className="text-lg text-muted-foreground mb-1">/ month</span>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">That's less than what one recovered implant case would pay for an entire year of the platform.</p>
          <div className="mt-6 space-y-3">
            {['Unlimited patient enrollments', 'Practice-branded portal', 'Credit monitoring from top bureaus', 'Intelligent guided improvement system', 'Onboarding and setup assistance'].map((f, i) => (
              <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{f}
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-8 h-12 text-base font-medium shadow-md"
              onClick={() => window.open('https://approvemysmile.com/register/personal', '_blank')}>
              Get Started Now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl px-6 h-12">Book a Demo First</Button>
            </Link>
          </div>
        </FadeInLeft>
        <FadeInRight>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-72 sm:h-96">
              <img src={IMAGES.womanSmiling} alt="Happy patient result" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-4 -right-4 sm:-right-6 bg-white rounded-xl shadow-[var(--shadow-md)] border border-[hsl(var(--border))] p-4 max-w-[200px]">
              <p className="text-2xl font-bold text-[hsl(var(--sss-teal))]">10x</p>
              <p className="text-xs text-muted-foreground">ROI from a single recovered case</p>
            </div>
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

/* ─── OBJECTION HANDLER ─── */
const ObjectionSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>Common Questions</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          Still on the Fence? Let's Address That.
        </h2>
      </FadeIn>
      <StaggerContainer className="grid gap-4 sm:grid-cols-2 max-w-5xl mx-auto">
        {[
          {
            q: '"My patients won\'t use it."',
            a: 'They already want treatment. They just can\'t afford it yet. The portal gives them a clear, guided path back — and 60% of people who start monitoring their credit see improvement. We keep them motivated and connected to your practice.'
          },
          {
            q: '"We already follow up with declined patients."',
            a: 'Manual follow-up has no system, no visibility, and no timing. Smile Score Solutions automates the entire journey and alerts your team at the exact moment a patient crosses a financing threshold. No guessing, no wasted calls.'
          },
          {
            q: '"What if patients don\'t qualify right away?"',
            a: 'Even patients who are early in their financial wellness journey stay engaged with your practice. The branded portal keeps them in your ecosystem instead of forgetting about you or going to a competitor. When they do qualify, you\'re the practice they return to.'
          },
          {
            q: '"Is $249/mo worth it for my practice?"',
            a: 'One recovered implant case is worth $5,000–$30,000+. The platform costs $2,988/year. If you recover just one additional case, you\'ve earned 1x–10x ROI. Most practices lose dozens of cases per month to declines.'
          },
          {
            q: '"Is this credit repair? Are there compliance issues?"',
            a: 'No. Smile Score Solutions is a financial wellness platform. We provide education, credit monitoring from top bureaus, and AI-powered guidance tools. Patients make their own informed decisions about their credit. Our platform is designed with compliance, privacy, and healthcare standards in mind.'
          },
          {
            q: '"How is this different from what\'s already out there?"',
            a: 'There is nothing like this in the dental market. No one else takes your declined patients and gives them a branded portal with real credit monitoring, AI-powered coaching, and automated nurture — then alerts your team when they\'re ready. We didn\'t just build a product. We built an entirely new category.'
          },
        ].map((item, i) => (
          <StaggerItem key={i}>
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow h-full">
              <CardContent className="p-6">
                <p className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-2">{item.q}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </CardContent>
            </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

/* ─── TRUST BAR ─── */
const TrustBarSection = () => (
  <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
          {[
            { icon: <CreditCard className="h-4 w-4" />, label: 'Credit Monitoring From Top Bureaus' },
            { icon: <Shield className="h-4 w-4" />, label: 'HIPAA-Conscious Platform' },
            { icon: <MonitorSmartphone className="h-4 w-4" />, label: 'Practice-Branded Experience' },
            { icon: <Lock className="h-4 w-4" />, label: 'Bank-Level Data Security' },
            { icon: <Heart className="h-4 w-4" />, label: 'No Cost to Patients' },
            { icon: <Wifi className="h-4 w-4" />, label: 'API & Automation Ready' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-2">
              <div className="h-12 w-12 rounded-xl bg-white shadow-[var(--shadow-sm)] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--sss-teal))]">{item.icon}</div>
              <span className="text-xs sm:text-sm text-[hsl(var(--sss-navy))]/80 font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── WHAT THIS IS / WHAT THIS ISN'T ─── */
const WhatThisIsSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>Full Transparency</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          What Smile Score Solutions Is and Isn't
        </h2>
      </FadeIn>
      <FadeIn>
        <div className="grid gap-6 sm:grid-cols-2 max-w-4xl mx-auto">
          <Card className="rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))]">What it is</h3>
              </div>
              <div className="space-y-3">
                {[
                  'An educational platform that teaches patients about credit, debt, and how to qualify for the dental care they need',
                  'A set of optional tools patients can choose to use: credit monitoring, an AI-generated personalized financial plan, and (where offered) credit dispute assistance',
                  'A practice-branded portal that keeps declined patients in your ecosystem',
                  'A revenue recovery system for dental practices',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <XCircle className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))]">What it isn't</h3>
              </div>
              <div className="space-y-3">
                {[
                  'A guarantee of any specific credit score result',
                  'A substitute for legal or financial advice',
                  'A way to remove accurate, current, verifiable information from a credit report. The law does not allow that, and neither do we',
                  'A service that acts on behalf of patients without their direct involvement',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-muted-foreground">
                    <XCircle className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ─── SEPARATION OF DUTIES ─── */
const SeparationSection = () => (
  <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] max-w-3xl mx-auto">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
              <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))]">Your Practice Is Protected</h3>
            </div>
            <p className="text-sm text-[hsl(var(--sss-ink))]/80 leading-relaxed">
              Smile Score Solutions contracts directly with patients for any paid services. Your practice subscribes to the platform; the patient relationship for any optional upgrades (credit monitoring, AI plan guidance, or credit dispute services) is between Smile Score Solutions and the patient. This protects your practice from regulatory complexity and keeps the value clean: you offer your patients a solution, we handle everything else.
            </p>
          </CardContent>
        </Card>
      </FadeIn>
    </div>
  </section>
);

/* ─── HOMEPAGE ─── */
export default function HomePage() {
  return (
    <div>
      <HeroSection />
      <WaitlistBanner />
      <SocialProofBar />
      <VideoSection />
      <WhyNowSection />
      <ProblemSection />
      <SolutionSection />
      <PatientPortalSection />
      <ComparisonSection />
      <HowItWorksBrief />
      <OutcomesSection />
      <FinancingPartnersSection />
      <AudienceSection />
      <TestimonialsSection />
      <PricingSnapshot />
      <ObjectionSection />
      <WhatThisIsSection />
      <SeparationSection />
      <TrustBarSection />
      <CTASection />
    </div>
  );
}
